import React, { useEffect, useState, useCallback } from "react";
import { Table, Input, Card, Space, Tooltip, Image, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import FilterDrawer from "../components/drawer";
import {
  DEFAULT_COMPANY_LOGO,
  IMAGE_HOST,
  INVESTOR_LOGO_HOST,
  LOGO_DEV_PUBLIC_KEY,
  recordActions,
} from "../../../../../constant";
import http from "../../../../../services/http";
import {
  Tune,
  Diamond,
  LooksOne,
  LooksTwo,
  Looks3,
  ChevronLeft,
  ChevronRight,
  InfoOutlined,
} from "@mui/icons-material";
import { salesforceOauth } from "../../../../../utils";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { debounce } from "lodash";

// Backend sends tier as strings: "Diamond", "Tier 1", "Tier 2", "Tier 3"
const TIER_KEYS = ["Diamond", "Tier 1", "Tier 2", "Tier 3"];

const TIER_OPTIONS = TIER_KEYS.map((t) => ({ value: t, label: t }));

const toTitleCaseWords = (value) =>
  String(value).replace(/\b([a-z])/g, (match) => match.toUpperCase());

const normalizeIndustryDisplayLabel = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  if (trimmed === trimmed.toLowerCase()) return toTitleCaseWords(trimmed);
  return trimmed;
};

const normalizeIndustryLabel = (value, preferredLabelsByKey = new Map()) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";

  const key = trimmed.toLowerCase();
  const preferred = preferredLabelsByKey.get(key);
  if (preferred) return preferred;

  return normalizeIndustryDisplayLabel(trimmed);
};

const normalizeIndustryValues = (value, preferredLabelsByKey = new Map()) => {
  if (!value) return [];
  return [...new Set(
    String(value)
    .split(/[,|]/)
    .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => normalizeIndustryLabel(item, preferredLabelsByKey)),
  )];
};

const getTierLabel = (tier) => {
  if (tier == null || tier === "") return "";
  const s = String(tier).trim();
  if (TIER_KEYS.includes(s)) return s;
  const n = Number(tier);
  if (n === 1) return "Diamond";
  if (n === 2) return "Tier 1";
  if (n === 3) return "Tier 2";
  if (n === 4) return "Tier 3";
  return s || `Tier ${tier}`;
};

// Map backend string (or legacy number) to icon and CSS class suffix
const TIER_CONFIG = {
  Diamond: { Icon: Diamond, classSuffix: "diamond" },
  "Tier 1": { Icon: LooksOne, classSuffix: "1" },
  "Tier 2": { Icon: LooksTwo, classSuffix: "2" },
  "Tier 3": { Icon: Looks3, classSuffix: "3" },
  1: { Icon: Diamond, classSuffix: "diamond" },
  2: { Icon: LooksOne, classSuffix: "1" },
  3: { Icon: LooksTwo, classSuffix: "2" },
  4: { Icon: Looks3, classSuffix: "3" },
};

function getTierConfig(tier) {
  if (tier == null || tier === "") return null;
  const label = getTierLabel(tier);
  return TIER_CONFIG[label] ?? TIER_CONFIG[tier] ?? TIER_CONFIG[Number(tier)] ?? null;
}
const InvestorDetailCol = ({ data }) => {
  // const [isLoading, setLoading] = useState(true);
  // const [logo, setLogo] = useState(null);
  // useEffect(() => {
  //   setLoading(true);
  //   fetchPrivateImage(data.avatar)
  //     .then((res) => {
  //       setLogo(res);
  //       setLoading(false);
  //     })
  //     .catch((err) => {
  //       setLoading(false);
  //     });
  // }, [data]);

  const tierLabel = getTierLabel(data?.tier);
  const tierCfg = getTierConfig(data?.tier);
  const TierIcon = tierCfg?.Icon;

  return (
    <div className="space-align-block">
      <Space align="center">
        {tierLabel && TierIcon ? (
          <Tooltip title={tierLabel} placement="top">
            <span
              className={`investor-tier-icon-wrap tier-${tierCfg?.classSuffix ?? "none"}`}
            >
              <TierIcon className="investor-tier-icon" fontSize="inherit" />
            </span>
          </Tooltip>
        ) : null}{" "}
        <span className={`buyer-list-thumb-wrap tier-${tierCfg?.classSuffix ?? "none"}`}>
          <span className="buyer-list-thumb">
            <Image
              preview={false}
              src={data?.avatar}
              fallback={IMAGE_HOST + DEFAULT_COMPANY_LOGO}
              alt={data.name}
            />
          </span>
        </span>
        {/* )} */}
        {/* {isLoading ? (
          <span className="buyer-list-thumb-box" />
        ) : (
          <img className="buyer-list-thumb" src={logo} alt={data.name} />
        )} */}
        <span className="mock-block buyers-list-name">{data.name}</span>
      </Space>
    </div>
  );
};

const InvestorsList = ({
  data = [],
  state = {},
  preferredIndustryPreview,
  onChange,
  selectedCompany = {},
  datalimit,
  total,
  page,
  pageChanger,
  search,
  setSearch,
  selectedFilter,
  setFilters,
  getInvestors,
  onPrev,
  onNext,
}) => {
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [investors, setInvestors] = useState([]);
  const [, setExportLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(search);
  const { search: queryParamSearch } = useLocation();
  const searchParams = new URLSearchParams(queryParamSearch);
  const exportToCRM = searchParams.get("connected");
  const companyPreferredIndustry =
    selectedCompany?.user_input?.[0]?.["industry_segmentation"]?.[1] ?? [];
  const preferredLabelsByKey = new Map(
    companyPreferredIndustry
      .map((industry) => String(industry ?? "").trim())
      .filter(Boolean)
      .map((industry) => [industry.toLowerCase(), normalizeIndustryDisplayLabel(industry)]),
  );
  const industryOptions = companyPreferredIndustry.map((industry) => ({
    value: industry,
    label: normalizeIndustryDisplayLabel(industry),
  }));
  const pagination = {
    pageSize: datalimit,
    showQuickJumper: false,
    position: ["bottomLeft"],
    total: total,
    showSizeChanger: false,
    current: page,
  };

  const filterToggle = () => {
    setFilterOpen(!isFilterOpen);
  };

  const setSearchHandler = useCallback(
    debounce((value) => setSearch(value), 500),
    [],
  );

  const searchHandler = (value) => {
    setSearchValue(value);
    setSearchHandler(value);
  };

  const exportToCRMHelper = async () => {
    setExportLoading(true);
    const params = {
      ...selectedFilter,
      search,
      offset: 0,
      crm_export: true,
    };
    Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
    const queryparams = new URLSearchParams(params).toString();
    http
      .get(`company-buyers/${selectedCompany?.id}?${queryparams}`)
      .then(({ data }) => {
        toast.success("Investors exported to CRM successfully");
        http.post("action-record", {
          action: recordActions.buyerCRMExport,
        });
      })
      .finally(() => {
        setExportLoading(false);
        searchParams.delete("connected");
        window.history.replaceState(null, "", window.location.pathname);
      })
      .catch((err) => {
        const isUnauthorized = err?.response?.status === 403;
        if (isUnauthorized) {
          salesforceOauth("/ai-recommendation");
          return;
        }
        toast.error(err?.response?.data?.error ?? "Failed to export to CRM");
      });
  };
  const init = () => {
    let list = [];
    data.forEach((item, i) => {
      const d = item?.programx_output;
      list.push({
        key: i,
        avatar: INVESTOR_LOGO_HOST + d.url + `?token=${LOGO_DEV_PUBLIC_KEY}`,
        name: d.company_name,
        tier: d.tier,
        // lead_name: d.lead_name,
        score: d.rank,
        type: d.type,
        user_preferred_industry_segmentation: d.user_preferred_industry_segmentation,
        // state: d.state,
        report: d.score,
        action: "",
      });
    });
    setInvestors(list);
  };
  const onSelectChange = (rows) => {
    onChange(rows);
  };

  useEffect(() => {
    init();
  }, [data]);

  useEffect(() => {
    exportToCRM && exportToCRMHelper();
  }, [exportToCRM]);

  const columns = [
    Table.SELECTION_COLUMN,
    {
      title: "Name",
      dataIndex: "",
      className: "detail-col",
      render: (item, index) => {
        return (
          <div
            className="space-align-container"
            onClick={() => onSelectChange([item.key])}
          >
            <InvestorDetailCol data={item} />
          </div>
        );
      },
    },
    {
      title: preferredIndustryPreview ? "Preferred Industry" : "",
      dataIndex: "user_preferred_industry_segmentation",
      render: (text) => {
        const industries = normalizeIndustryValues(text, preferredLabelsByKey);
        const primaryIndustry = industries[0] || "-";
        const extraCount = Math.max(industries.length - 1, 0);
        if (!preferredIndustryPreview) return <></>;
        return (
          <Tooltip title={industries.length ? industries.join(", ") : "-"}>
            <div className="industry-cell">
              <span className="industry-pill">{primaryIndustry}</span>
              {extraCount > 0 && (
                <span className="industry-pill-count">+{extraCount}</span>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      className: "report-col llm-rank-col",
      title: (
        <span className="llm-rank-header">
          LLM Rank
          <Tooltip title="LLM Rank is AI-generated confidence score for this target. Higher is better.">
            <span className="hint-icon">
              <InfoOutlined fontSize="inherit" />
            </span>
          </Tooltip>
        </span>
      ),
      width: 130,
      align: "right",
      dataIndex: "",
      render: (data) => {
        return (
          <button className="llm-rank-badge" onClick={() => onSelectChange([data.key])}>
            {data.score ?? "-"}
          </button>
        );
      },
    },
  ];

  const getRowClassName = (record) => {
    const tierCfg = getTierConfig(record?.tier);
    if (tierCfg?.classSuffix === "diamond") return "investor-row-tier-diamond";
    return "";
  };

  const isBtnDisabled = data?.length < 1;
  return (
    <React.Fragment>
      <Card className="buyers-list florence-list-card">
        <div className="search-input investor-search-input investors-list-toolbar buyers-list-filters">
          <div className="input-box investors-list-search-group">
            <Input
              value={searchValue}
              size={90}
              placeholder="Name"
              prefix={<SearchOutlined />}
              onChange={(e) => searchHandler(e.target.value)}
            />
            <Tooltip title="More filters">
              <span className="search-icon" onClick={filterToggle}>
                <Tune />
              </span>
            </Tooltip>
          </div>
          <Select
            className="investor-tier-filter"
            placeholder="Tier"
            mode="multiple"
            allowClear
            style={{ minWidth: 140 }}
            value={
              Array.isArray(selectedFilter?.tier)
                ? selectedFilter.tier
                : selectedFilter?.tier
                  ? [selectedFilter.tier]
                  : undefined
            }
            onChange={(value) => {
              const next = { ...selectedFilter };
              if (value == null || value.length === 0) delete next.tier;
              else next.tier = value;
              setFilters(Object.keys(next).length ? next : null);
            }}
            options={TIER_OPTIONS}
          />
          {preferredIndustryPreview && (
            <Select
              className="investor-industry-filter"
              placeholder="Industry"
              mode="multiple"
              allowClear
              style={{ minWidth: 140 }}
              value={
                Array.isArray(selectedFilter?.industry_bucket)
                  ? selectedFilter.industry_bucket
                  : selectedFilter?.industry_bucket
                    ? [selectedFilter.industry_bucket]
                    : undefined
              }
              onChange={(value) => {
                const next = { ...selectedFilter };
                if (value == null || value.length === 0) delete next.industry_bucket;
                else next.industry_bucket = value;
                setFilters(Object.keys(next).length ? next : null);
              }}
              options={industryOptions}
            />
          )}
        </div>
        <Table
          size="middle"
          className="investors-table"
          rowClassName={getRowClassName}
          rowSelection={{
            type: "radio",
            selectedRowKeys: state.selectedRowKeys,
            onChange: onSelectChange,
          }}
          columns={columns}
          dataSource={investors}
          pagination={{ ...pagination, onChange: pageChanger }}
          showSizeChanger={false}
        />
        <div className="buyers-list-footer">
          <div className="buyers-list-footer-nav">
            <button type="button" onClick={onPrev} disabled={isBtnDisabled}>
              <ChevronLeft />
            </button>
            <button type="button" onClick={onNext} disabled={isBtnDisabled}>
              <ChevronRight />
            </button>
          </div>
        </div>
      </Card>
      <FilterDrawer
        isOpen={isFilterOpen}
        toggle={filterToggle}
        isRemoved={false}
        setFilter={setFilters}
      />
    </React.Fragment>
  );
};

export default InvestorsList;
